import React, { useState, useMemo, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import "./Dashboard.scss";
import { Link } from "react-router-dom";
import ClaimsChart from "./ClaimsChart";
import { getDashboard } from "../../../services/dashboardService";

function getMonthParam(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function getMonthLabel(monthParam) {
  const [y, m] = monthParam.split("-").map(Number);
  const date = new Date(y, m - 1, 1);
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

export default function Dashboard() {
  const [monthParam, setMonthParam] = useState(() => getMonthParam(new Date()));
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  const selectedMonthDate = useMemo(() => {
    const [year, month] = monthParam.split("-").map(Number);
    return new Date(year, month - 1, 1);
  }, [monthParam]);

  const [visibleYear, setVisibleYear] = useState(
    selectedMonthDate.getFullYear(),
  );

  useEffect(() => {
    setVisibleYear(selectedMonthDate.getFullYear());
  }, [selectedMonthDate]);

  useEffect(() => {
    function handleOutsideClick(event) {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsMonthPickerOpen(false);
      }
    }

    if (isMonthPickerOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isMonthPickerOpen]);

  const {
    data: dashboardResponse,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["dashboard", monthParam],
    queryFn: () => getDashboard(monthParam),
    retry: 1,
    onError: (err) => {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load dashboard";
      toast.error(msg);
    },
  });

  const apiData = dashboardResponse?.data;
  const metrics = apiData?.metrics || {};
  const claimsChart = apiData?.claimsChart || [];
  const pendingPlates = apiData?.pendingPlates || [];
  const approvedPlates = apiData?.approvedPlates || [];
  const otherStatusPlates = apiData?.otherStatusPlates || [];
  const latestPlates = apiData?.latestPlates || [];

  const cards = useMemo(
    () => [
      { title: "Total Users", value: metrics.totalUsers ?? "—" },
      { title: "Businesses", value: metrics.businesses ?? "—" },
      { title: "Verified Plates", value: metrics.verifiedPlates ?? "—" },
      { title: "Pending Reports", value: metrics.pendingReports ?? "—" },
    ],
    [metrics],
  );

  const plateClaimsPendingReview = useMemo(
    () => ({
      title: "Plate Claims Pending Review",
      columns: ["Plate #", "User", "Action"],
      rows: pendingPlates.map((row) => ({
        plateNumber: row.plateNumber || "—",
        user: row.user || "—",
        action: "Review",
        profileId: row.profileId,
      })),
    }),
    [pendingPlates],
  );

  const businessVerificationsPending = useMemo(
    () => ({
      title: "Plates Claims Approved Review",
      columns: ["Plate #", "User", "Action"],
      rows: approvedPlates.map((row) => ({
        plateNumber: row.plateNumber || "—",
        user: row.user || "—",
        action: "Review",
        profileId: row.profileId,
      })),
    }),
    [approvedPlates],
  );

  const reportsAwaitingAction = useMemo(
    () => ({
      title: "Plates Claims Approved Rejected",
      columns: ["Plate #", "Reason", "Action"],
      rows: otherStatusPlates.map((row) => ({
        plateNumber: row.plateNumber || "—",
        reason: formatStatus(row.status),
        action: "Review",
        profileId: row.profileId,
      })),
    }),
    [otherStatusPlates],
  );

  const recentActivity = useMemo(
    () => ({
      title: "Recent Activity Log",
      columns: ["User", "Admin", "Action", "Target", "Result"],
      rows: latestPlates.map((row) => ({
        user: {
          name: row.user || "—",
          email: "—",
        },
        admin: "—",
        action: "Plate activity",
        target: row.plateNumber || "—",
        result: formatStatus(row.status),
        resultClass: statusToResultClass(row.status),
      })),
    }),
    [latestPlates],
  );

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, idx) => {
        const date = new Date(visibleYear, idx, 1);
        return {
          param: getMonthParam(date),
          shortLabel: date.toLocaleDateString("en-US", { month: "short" }),
        };
      }),
    [visibleYear],
  );

  const currentMonthLabel = getMonthLabel(monthParam);

  function formatStatus(status) {
    if (!status) return "—";
    return status
      .split("_")
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
      .join(" ");
  }

  function statusToResultClass(status) {
    if (!status) return "";
    const s = status.toLowerCase();
    if (s === "verified" || s === "approved" || s === "resolved")
      return "success";
    if (s === "suspended" || s === "rejected") return "suspended";
    if (s === "revoked") return "revoked";
    if (s === "pending" || s === "information_requested") return "pending";
    return "";
  }

  if (isLoading) {
    return (
      <React.Fragment>
        <header className="panel-header items-center flex content-justify-between">
          <div className="left-side">
            <h4 className="title">Dashboard</h4>
          </div>
        </header>
        <div className="dashboard-cards">
          <div className="loader-container">
            <div className="loader"></div>
            <p>Loading dashboard...</p>
          </div>
        </div>
      </React.Fragment>
    );
  }

  if (isError) {
    return (
      <React.Fragment>
        <header className="panel-header items-center flex content-justify-between">
          <div className="left-side">
            <h4 className="title">Dashboard</h4>
          </div>
        </header>
        <div className="dashboard-cards">
          <div className="error-container">
            <p>Error loading dashboard. Please try again.</p>
            {error && (
              <small>{error?.response?.data?.message || error?.message}</small>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <header className="panel-header items-center flex content-justify-between">
        <div className="left-side">
          <h4 className="title">Dashboard</h4>
        </div>
        <div className="right-side">
          <div className="month-picker" ref={pickerRef}>
            <button
              type="button"
              className="month-picker-toggle"
              onClick={() => setIsMonthPickerOpen((prev) => !prev)}
            >
              {currentMonthLabel}
            </button>
            {isMonthPickerOpen && (
              <div className="month-picker-popover">
                <div className="month-picker-header">
                  <button
                    type="button"
                    className="year-nav-btn"
                    onClick={() => setVisibleYear((prev) => prev - 1)}
                    aria-label="Previous year"
                  >
                    &lt;
                  </button>
                  <span>{visibleYear}</span>
                  <button
                    type="button"
                    className="year-nav-btn"
                    onClick={() => setVisibleYear((prev) => prev + 1)}
                    aria-label="Next year"
                  >
                    &gt;
                  </button>
                </div>
                <div className="month-grid">
                  {monthOptions.map((opt) => (
                    <button
                      key={opt.param}
                      type="button"
                      className={`month-cell ${monthParam === opt.param ? "active" : ""}`}
                      onClick={() => {
                        setMonthParam(opt.param);
                        setIsMonthPickerOpen(false);
                      }}
                    >
                      {opt.shortLabel}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
      <div className="dashboard-cards">
        <div className="row">
          {cards.map((item, index) => (
            <div key={index} className="col-lg-3 col-md-6">
              <div className="card">
                <p>{item.title}</p>
                <h4>{item.value}</h4>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* <ClaimsChart claimsChart={claimsChart} /> */}
      {/* <div className="dashboard-reports">
        <div className="row">
          {[
            plateClaimsPendingReview,
            businessVerificationsPending,
            reportsAwaitingAction,
          ].map((tableData, tIndex) => (
            <div key={tIndex} className="col-lg-4">
              <div className="card">
                <h4>{tableData.title}</h4>
                <div className="table-responsive">
                  <table className="table table-borderless">
                    <thead>
                      <tr>
                        {tableData.columns.map((col, cIndex) => (
                          <th key={cIndex}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.rows.map((row, rIndex) => (
                        <tr key={rIndex}>
                          {tableData.columns.map((col, cIndex) => {
                            const key = col
                              .toLowerCase()
                              .replace(/ /g, "")
                              .replace("#", "Number");
                            const actionHref =
                              tIndex === 0
                                ? "/plates-management/pending-plates-claims"
                                : tIndex === 1
                                  ? "/plates-management/approved-plates"
                                  : "/plates-management/reported-plates";
                            return (
                              <td key={cIndex}>
                                {row[key] != null && row[key] !== "" ? (
                                  key === "action" ? (
                                    <Link to={actionHref}>{row[key]}</Link>
                                  ) : (
                                    row[key]
                                  )
                                ) : (
                                  "—"
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
          <div className="col-lg-12">
            <div className="card">
              <h4>{recentActivity.title}</h4>
              <div className="table-responsive">
                <table className="table table-borderless">
                  <thead>
                    <tr>
                      {recentActivity.columns.map((col, cIndex) => (
                        <th key={cIndex}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recentActivity.rows.map((row, rIndex) => (
                      <tr key={rIndex}>
                        <td>
                          <span>{row.user?.name ?? "—"}</span>
                          <br />
                          <small>{row.user?.email ?? ""}</small>
                        </td>
                        <td>{row.admin ?? "—"}</td>
                        <td>{row.action ?? "—"}</td>
                        <td>{row.target ?? "—"}</td>
                        <td>
                          <span
                            className={`resultClass ${row.resultClass || ""}`}
                          >
                            <span className="dot"></span>
                            {row.result ?? "—"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div> */}
    </React.Fragment>
  );
}
